import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * W25: Cloudflare Turnstile on the public web forms (sign-in code request, area vote).
 *
 * Browser only: the native app is exempt on the server by its W24 integrity pass, so inside
 * Capacitor there is no widget and no header. An empty VITE_TURNSTILE_SITE_KEY also turns it off
 * (local dev keeps working without Cloudflare). Tokens are single-use: call `reset()` after every
 * attempt, successful or not.
 */

interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'expired-callback': () => void;
  'error-callback': () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'flexible' | 'compact';
}

interface TurnstileApi {
  render(element: HTMLElement, options: TurnstileRenderOptions): string;
  reset(widgetId?: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let loading: Promise<TurnstileApi> | null = null;

/** Injects the Turnstile script once per page; later callers share the same promise. */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loading) return loading;
  loading = new Promise<TurnstileApi>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    const script = existing ?? document.createElement('script');
    script.addEventListener(
      'load',
      () => {
        if (window.turnstile) resolve(window.turnstile);
        else {
          loading = null;
          reject(new Error('turnstile_unavailable'));
        }
      },
      { once: true },
    );
    script.addEventListener(
      'error',
      () => {
        loading = null;
        script.remove();
        reject(new Error('turnstile_unavailable'));
      },
      { once: true },
    );
    if (!existing) {
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
  return loading;
}

const SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '').trim();

/** True when this build and platform show the Turnstile widget. */
export function turnstileEnabled(): boolean {
  return SITE_KEY !== '' && !Capacitor.isNativePlatform();
}

export interface TurnstileState {
  /** Render the widget (browser build with a site key). */
  enabled: boolean;
  /** The current single-use token, or null while the check is not done. */
  token: string | null;
  /** The widget could not load or reported an error. */
  failed: boolean;
  /** Attach to the widget's container element. */
  ref: (element: HTMLDivElement | null) => void;
  /** Forget the token and run the check again (after every attempt). */
  reset: () => void;
}

export function useTurnstile(): TurnstileState {
  const enabled = turnstileEnabled();
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !element) return;
    let cancelled = false;
    const dark = document.documentElement.classList.contains('night');
    loadTurnstile()
      .then((api) => {
        if (cancelled) return;
        setFailed(false);
        widgetId.current = api.render(element, {
          sitekey: SITE_KEY,
          theme: dark ? 'dark' : 'light',
          size: 'flexible',
          callback: (value) => {
            setFailed(false);
            setToken(value);
          },
          'expired-callback': () => setToken(null),
          'error-callback': () => {
            setToken(null);
            setFailed(true);
          },
        });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      const id = widgetId.current;
      widgetId.current = null;
      setToken(null);
      if (id && window.turnstile) window.turnstile.remove(id);
    };
  }, [enabled, element]);

  const reset = useCallback(() => {
    setToken(null);
    const id = widgetId.current;
    if (id && window.turnstile) window.turnstile.reset(id);
  }, []);

  return { enabled, token, failed, ref: setElement, reset };
}
